import IntlMessageFormat from "intl-messageformat"
import IntlRelativeFormat from "intl-relativeformat"
import createFormatCache from "intl-format-cache"


const _isDate = require("lodash/isDate");
const _isString = require("lodash/isString");
const _isNumber = require("lodash/isNumber");
const _isPlainObject = require("lodash/isPlainObject");
const _each = require("lodash/each");
const _kebabCase = require("lodash/kebabCase");
const _clamp = require("lodash/clamp");


/*
 ===========================================================================
 Copyright 2015-2016 Sebastian Software GmbH, Germany
 Licensed under Apache-2.0
 ===========================================================================
 */

function install(Vue, options) {
	const formats = IntlMessageFormat.formats;

	const getCachedNumberFormat = createFormatCache(Intl.NumberFormat);
	const getCachedDateTimeFormat = createFormatCache(Intl.DateTimeFormat);
	const getCachedMessageFormat = createFormatCache(IntlMessageFormat);
	const getCachedRelativeFormat = createFormatCache(IntlRelativeFormat);

	// A constant defined by the standard Intl.NumberFormat
	// const maximumFractionDigits = 20;
	// Unfortunately through formatting issues of percent values in IE
	// we have to use a small value here, because IE (as of v11) seems to
	// account the percent symbol + optional space to the fraction digits.
	// See also: https://github.com/sebastian-software/vue-locale/issues/1#issuecomment-215396481
	const maximumFractionDigits = 18;

	const language = options.language;
	const currency = options.currency;
	const messages = options.messages;

	const locale = language;

	// =============================================
	//   FORMATTER FUNCTIONS
	// =============================================

	function formatDate(date, format) {
		const parsedDate = new Date(date);
		if (!_isDate(parsedDate))
			throw new TypeError("A date or timestamp must be provided to {{formatDate}}");

		if (_isString(format) && format in formats.date)
			format = formats.date[format];

		return getCachedDateTimeFormat(locale, format).format(parsedDate);
	}

	function formatTime(date, format) {
		const parsedDate = new Date(date);
		if (!_isDate(date))
			throw new TypeError("A date or timestamp must be provided to {{formatTime}}");

		if (_isString(format) && format in formats.time)
			format = formats.time[format];

		return getCachedDateTimeFormat(locale, format).format(parsedDate);
	}

	function formatNumber(num, format) {
		if (!_isNumber(num))
			throw new TypeError("A number must be provided to {{formatNumber}}");

		if (_isString(format)) {
			if (format === "currency") {
				format = {
					style: "currency",
					currency: currency
				}
			} else if (format in formats.number) {
				format = formats.number[format];
			}
		}

		return getCachedNumberFormat(locale, format).format(num);
	}

	function formatRelative(date, format, now) {
		const parsedDate = new Date(date);
		if (!_isDate(parsedDate)) throw new TypeError("A date or timestamp must be provided to {{formatRelative}}");

		return getCachedRelativeFormat(locale, format).format(parsedDate, {
			now: now || new Date()
		});
	}

	function formatCurrency(val, currency) {
		const numberOptions = {
			style: "currency",
			currency: 'EUR',
			minimumFractionDigits: 2,
			maximumFractionDigits: 2
		};

		const result = formatNumber(!val ? 0 : val, numberOptions);
		return result.replace('€', currency);
	}

	function formatMessage(message) {
		if (message.startsWith('##'))
			return message.slice(2);
		const _len = arguments.length;
		let formatOptions = Array(_len > 1 ? _len - 1 : 0);
		for (let _key = 1; _key < _len; _key++) {
			formatOptions[_key - 1] = arguments[_key];
		}

		// Read real message from DB
		if (message in messages) message = messages[message];

		if (typeof message === "string") message = getCachedMessageFormat(message, locale, {});

		// If there is a single map parameter, use that instead of the formatOptions array
		if (formatOptions.length === 1 && _isPlainObject(formatOptions[0])) formatOptions = formatOptions[0];

		return message.format(formatOptions);
	}

	// =============================================
	//   REGISTER FILTERS
	// =============================================

	const helpers = {
		formatDate,
		formatTime,
		formatRelative,
		formatNumber,
		formatMessage,
		formatCurrency
	};

	_each(helpers, function (helper, name) {
		// Adding features as a VueJS filter for easily pass a string over (only numberic parameters though)
		Vue.filter(_kebabCase(name), helper);

		// Support alternative full blown calling of methods with real options object
		Vue.prototype["$" + name] = helper;
	});

	Vue.prototype.$locale = locale;

	Vue.directive("i18n", function (el, binding) {
		el.innerHTML = formatMessage(binding.value);
	});

	// The following causes a Vue error ("text" cannot be used as a component id)

	// Vue.component("text", {
	//   template: '<span class="text">{{{ translated }}}</span>',
	//   props: {
	//     msg: {
	//       type: String,
	//       required: true
	//     }
	//   },
	//   computed: {
	//     translated: function translated() {
	//       for (var _len2 = arguments.length, formatOptions = Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
	//         formatOptions[_key2] = arguments[_key2];
	//       }
	//
	//       return formatMessage.apply(undefined, [this.msg].concat(formatOptions));
	//     }
	//   }
	// });

	// =============================================
	//   ADDITIONAL FILTERS
	// =============================================

	Vue.filter("format-currency", function (val, currency) {
		return formatCurrency(val, currency);
	});

	Vue.filter("format-percent", function read(val, fractionDigits) {
		return formatNumber(val == null || val === "" ? 0 : _clamp(val / 100, 0, 1), {
			style: "percent",
			minimumFractionDigits: fractionDigits == null ? 0 : fractionDigits,
			maximumFractionDigits: fractionDigits == null ? maximumFractionDigits : fractionDigits
		});
	});

	Vue.filter("format-number", function read(val, fractionDigits) {
		return val == null || val === "" ? 0 : formatNumber(val, {
			minimumFractionDigits: fractionDigits == null ? 0 : fractionDigits,
			maximumFractionDigits: fractionDigits == null ? maximumFractionDigits : fractionDigits
		});
	});
}

export default {
	install
}

export async function init(locale) {
	window.IntlRelativeFormat = IntlRelativeFormat;
	let langPromise;
	if (locale === 'fr') {
		langPromise = import(/* webpackChunkName: "fr" */ 'intl-relativeformat/dist/locale-data/fr.js');
	} else {
		langPromise = import(/* webpackChunkName: "en" */ 'intl-relativeformat/dist/locale-data/en.js');
	}
	return langPromise;
}


// WEBPACK FOOTER //
// ./src/utils/i18n.js